import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormHelperText,
  FormLabel,
  Group,
  Input,
  Select,
  Textarea
} from '@screentone/core';

import { ImageManagerEmbed } from 'components/image-manager-embed/ImageManagerEmbed';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { GraphicLinkedItem, LinkedItem, LinkedItemCardType, MobileLinkedItem, Visual } from 'data/generated/graphql';
import useControlledQuery from 'hooks/altsumm/useControlledQuery';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useAuthToken } from 'hooks/useAuthToken';
import { useDebounce } from 'hooks/useDebounce';
import { SnippetyImageData, SnippetyResponse, SnippetyResponseWithImage } from 'hooks/useSnippetyQuery';
import { GRAPHIC_LINKED_ITEM, MOBILE_LINKED_ITEM } from 'utils/constants';
import { isValidURL } from 'utils/url';
import {
  DEFAULT_MOBILE_LINKED_ITEM_ATTRIBUTES,
  getProductForUrl,
  getTitleForUrl,
  hasBlockedDomain
} from './LinkedItem.constants';
import styles from './LinkedItemForm.module.scss';

interface LinkedItemFormProps {
  defaultHeadline: string;
  defaultMedia?: { name: string } | null;
  defaultSummary?: string | null;
  defaultValidationBypassed?: boolean;
  defaultUrl: string;
  defaultCtaLink?: string;
  defaultCtaText?: string;
  hierarchyId: string;
  uuid: string;
  isMobileLinkedItem?: boolean;
  isGraphicLinkedItem?: boolean;
  isReadOnly?: boolean;
}

const urlErrorMessages = {
  notFound: 'Cannot get data from this link, you need to manually input the data.',
  invalidUrl: 'Invalid URL!',
  notExternal: 'Link items are only for external resources! Please use Content Items instead.'
};

const imageErrorMessages = {
  notFound: 'Image not found!',
  invalidID: 'Invalid Image ID. ID format must be "im-[some numbers]"'
};

const IM_ID_REGEX = /[iI][mM]-\d{4,}/i;
const ONLY_IM_ID = new RegExp(`^${IM_ID_REGEX.source}$`);

// Allowed URL protocols for linked items
const ALLOWED_PROTOCOLS = ['http://', 'https://'];

const scrollToItem = (id: string) => {
  requestAnimationFrame(() => {
    document
      .querySelector(`[data-model-hierarchy-id="${id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
};

export const LinkedItemForm = ({
  defaultHeadline,
  defaultMedia,
  defaultSummary,
  defaultUrl,
  defaultValidationBypassed,
  defaultCtaLink = '',
  defaultCtaText = '',
  hierarchyId,
  uuid,
  isMobileLinkedItem = false,
  isGraphicLinkedItem = false,
  isReadOnly = false
}: LinkedItemFormProps) => {
  const authToken = useAuthToken();
  const defaultImId = defaultMedia?.name.match(IM_ID_REGEX)?.[0] ?? '';

  const [errors, setErrors] = useState<string[]>([]);
  const [cardType, setCardType] = useState<LinkedItemCardType>(
    isGraphicLinkedItem ? LinkedItemCardType.Graphics : LinkedItemCardType.Standard
  );
  const [headline, setHeadline] = useState(defaultHeadline);
  const [media, setMedia] = useState(defaultImId);
  const [summary, setSummary] = useState(defaultSummary ?? '');
  const [currentUrl, setCurrentUrl] = useState(defaultUrl);
  const [previousUrl, setPreviousUrl] = useState('');
  const [bypassValidation, setBypassValidation] = useState<boolean>(!!defaultValidationBypassed);
  const [mediaValidationError, setMediaValidationError] = useState('');
  const [urlValidationError, setCurrentUrlValidationError] = useState<string | null>(null);
  const [disableMetadata, setDisableMetadata] = useState(false);
  const [graphicUrl, setGraphicUrl] = useState(defaultUrl);
  const [ctaLink, setCtaLink] = useState(defaultCtaLink);
  const [ctaText, setCtaText] = useState(defaultCtaText);
  const [graphicUrlError, setGraphicUrlError] = useState('');

  const { currentProperty } = useAuth();
  const { data } = usePublicationSettings();

  // Debounce URL to avoid excessive snippety calls while typing
  const debouncedUrl = useDebounce(currentUrl, 500);
  const debouncedGraphicUrl = useDebounce(graphicUrl, 500);

  const {
    value: imageValue,
    setControlledQuery: setControlledQueryImage,
    data: imageSnippetyData,
    error: imageSnippetyError,
    isLoading: isFetchingImageDataFromSnippety
  } = useControlledQuery('image', { value: '' }, authToken, data?.publicationSetting);

  const {
    value: urlValue,
    setControlledQuery: setControlledQueryUrl,
    data: urlSnippetyData,
    error: urlSnippetyError,
    isLoading: isFetchingUrlDataFromSnippety
  } = useControlledQuery('url', { value: '' }, authToken, data?.publicationSetting);

  const { setIsEditingLinkedItem, removeEntity, insertEntity, modifyEntity, getEntity } = useDataModelContext();
  const closeEditingForm = () =>
    setIsEditingLinkedItem((prev) => {
      const next = new Set(prev);
      next.delete(uuid);
      return next;
    });

  const formRef = useRef<HTMLDivElement>(null);
  const totalWords = summary.length ? summary.trim().split(' ').length : 0;
  const isEditingExistingLinkedItem = !!defaultUrl;
  const showBypassValidationCheckbox = urlValidationError === urlErrorMessages.notExternal;

  // Scroll the form into view when a new linked item is added
  useEffect(() => {
    if (!isEditingExistingLinkedItem) {
      requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typedUrlSnippetyData =
    !urlSnippetyError && urlSnippetyData ? (urlSnippetyData as SnippetyResponse | null) : null;
  const typedImageSnippetyData =
    !imageSnippetyError && imageSnippetyData ? (imageSnippetyData as SnippetyResponseWithImage | null) : null;

  const setData = useCallback(() => {
    if (typedUrlSnippetyData?.title) {
      setHeadline(typedUrlSnippetyData.title);
    }
    if (typedUrlSnippetyData?.desc) {
      setSummary(typedUrlSnippetyData.desc);
    }
  }, [typedUrlSnippetyData]);

  const validateAndQueryUrl = useCallback(
    (url: string) => {
      if (url === '') {
        setCurrentUrlValidationError(null);
        setDisableMetadata(false);
        return;
      }

      // Validate that URL is valid and uses an allowed protocol
      const hasAllowedProtocol = ALLOWED_PROTOCOLS.some((protocol) => url.startsWith(protocol));
      if (!isValidURL(url) || !hasAllowedProtocol) {
        setCurrentUrlValidationError(urlErrorMessages.invalidUrl);
        return;
      }

      // Check if this URL should have metadata disabled
      const shouldDisableMetadata = hasBlockedDomain(url);
      setDisableMetadata(shouldDisableMetadata);

      // Skip snippety call for blocked domains (e.g., polymarket.com)
      // Also reset any stale snippety state from a previously-typed non-blocked URL.
      if (shouldDisableMetadata) {
        setCurrentUrlValidationError(null);
        setControlledQueryUrl({ value: '' });
        setPreviousUrl(url);
        return;
      }

      // Don't clear the error here - let snippety response handle it
      // This prevents clearing errors before snippety responds

      // Trigger snippety query if URL is valid and different from previous
      if (url !== previousUrl) {
        setControlledQueryUrl({ value: url });
        setPreviousUrl(url);
      }
    },
    [previousUrl, setControlledQueryUrl]
  );

  const handleUrlOnBlur = () => {
    validateAndQueryUrl(currentUrl);
  };

  const handleUrlPaste = () => {
    // On paste, immediately validate and query (don't wait for debounce)
    // We use setTimeout to ensure the pasted value is in the input
    setTimeout(() => {
      validateAndQueryUrl(currentUrl);
    }, 0);
  };

  const handleImageOnBlur = () => {
    if (media === '') {
      setMediaValidationError('');
      setControlledQueryImage({ value: '' });
    } else if (media && ONLY_IM_ID.test(media)) {
      setMediaValidationError('');
      setControlledQueryImage({ value: media });
    } else if (media) {
      setMediaValidationError(imageErrorMessages.invalidID);
    }
  };

  const embedCallback = useCallback(
    (response: SnippetyImageData) => {
      const imageId = response.src?.imageId ?? `im-${response.id}`;
      setMedia(imageId);
      setMediaValidationError('');
      setControlledQueryImage({ value: imageId });
    },
    [setControlledQueryImage]
  );

  const handleBypassValidation = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBypassValidation(e.target.checked);
    if (e.target.checked) {
      setCurrentUrlValidationError(null);
      setData();
    } else {
      setCurrentUrlValidationError(urlErrorMessages.notExternal);
    }
  };

  useEffect(() => {
    // Check if the default URL should have metadata disabled on mount
    const shouldDisableMetadata = hasBlockedDomain(defaultUrl);
    if (shouldDisableMetadata) {
      setDisableMetadata(true);
    }
  }, [defaultUrl]);

  useEffect(() => {
    const raw = data?.publicationSetting.defaultLinkedItemType;
    if (isEditingExistingLinkedItem || !raw) return;
    setCardType(raw);
  }, [data, isEditingExistingLinkedItem]);

  // Trigger snippety query when user stops typing (debounced)
  useEffect(() => {
    if (debouncedUrl && debouncedUrl !== defaultUrl && debouncedUrl !== previousUrl) {
      validateAndQueryUrl(debouncedUrl);
    }
  }, [debouncedUrl, defaultUrl, previousUrl, validateAndQueryUrl]);

  // Handle image snippety queries
  useEffect(() => {
    if (!typedImageSnippetyData && media && !mediaValidationError && ONLY_IM_ID.test(media)) {
      setControlledQueryImage({ value: media });
    } else if (!media) {
      setControlledQueryImage({ value: '' });
    }
  }, [media, typedImageSnippetyData, setControlledQueryImage, mediaValidationError]);

  useEffect(() => {
    if (imageSnippetyError?.error) {
      setMediaValidationError(imageErrorMessages.notFound);
    }
    if (urlSnippetyError?.error && !hasBlockedDomain(currentUrl)) {
      setCurrentUrlValidationError(urlErrorMessages.notFound);
    }
  }, [urlSnippetyError, imageSnippetyError, currentUrl]);

  useEffect(() => {
    if (!typedUrlSnippetyData) {
      return;
    }

    if (currentProperty && typedUrlSnippetyData.provider?.includes(currentProperty) && !bypassValidation) {
      setCurrentUrlValidationError(urlErrorMessages.notExternal);
      return;
    }
    setCurrentUrlValidationError(null);
    setData();
  }, [currentProperty, setData, typedUrlSnippetyData, bypassValidation]);

  useEffect(() => {
    if (!debouncedGraphicUrl) {
      setGraphicUrlError('');
      return;
    }
    if (!isValidURL(debouncedGraphicUrl) || !ALLOWED_PROTOCOLS.some((p) => debouncedGraphicUrl.startsWith(p))) {
      setGraphicUrlError(urlErrorMessages.invalidUrl);
      return;
    }
    setGraphicUrlError('');
  }, [debouncedGraphicUrl]);

  // For blocked domains (polymarket.com), we drop headline validation skip snippety calls, so don't check snippety-related state
  const isBlockedDomain = hasBlockedDomain(currentUrl);

  const validation: string[] = [
    !isBlockedDomain && !headline && !disableMetadata && 'headline',
    !currentUrl && 'url',
    media && !IM_ID_REGEX.test(media) && 'media'
  ].filter((v: string | false): v is string => !!v);

  const ctaLinkError =
    ctaLink && (!isValidURL(ctaLink) || !ALLOWED_PROTOCOLS.some((p) => ctaLink.startsWith(p)))
      ? urlErrorMessages.invalidUrl
      : '';

  const isGraphicUrlValid =
    !!graphicUrl && isValidURL(graphicUrl) && ALLOWED_PROTOCOLS.some((p) => graphicUrl.startsWith(p));

  const graphicsValidation: string[] = [!isGraphicUrlValid && 'url', !!ctaLinkError && 'ctaLink'].filter(
    (v: string | false): v is string => !!v
  );

  const handleSubmit = () => {
    if (cardType === LinkedItemCardType.Graphics) {
      const graphicItem = {
        type: 'LinkedItem',
        subtype: GRAPHIC_LINKED_ITEM,
        uuid,
        attributes: {
          type: 'link',
          id: graphicUrl,
          hosted_url: graphicUrl,
          // Graphic cards always serialize visual as primary;
          visual: Visual.Primary,
          ctaLink: ctaLink || null,
          ctaText: ctaText || null
        }
      };

      if (isEditingExistingLinkedItem) {
        modifyEntity(hierarchyId, (entity) => {
          const existing = entity as GraphicLinkedItem;
          Object.assign(entity, {
            ...graphicItem,
            attributes: { ...existing.attributes, ...graphicItem.attributes }
          });
        });
      } else {
        const existingSize = getEntity<GraphicLinkedItem>(hierarchyId)?.attributes.size;
        removeEntity(hierarchyId);
        insertEntity(hierarchyId, {
          ...graphicItem,
          attributes: { ...graphicItem.attributes, ...(existingSize ? { size: existingSize } : {}) }
        } as GraphicLinkedItem);
      }

      closeEditingForm();
      scrollToItem(hierarchyId);
      return;
    }

    const sharedValues = {
      type: 'LinkedItem',
      uuid,
      attributes: {
        type: 'link',
        id: currentUrl,
        hosted_url: currentUrl,
        product: isBlockedDomain ? getProductForUrl(currentUrl) : (typedUrlSnippetyData?.provider ?? ''),
        title: isBlockedDomain ? getTitleForUrl(currentUrl) : headline,
        description: isBlockedDomain ? null : summary
      }
    };
    let linkedItem: LinkedItem | MobileLinkedItem;
    if (isMobileLinkedItem) {
      linkedItem = {
        ...sharedValues,
        subtype: MOBILE_LINKED_ITEM,
        attributes: {
          ...sharedValues.attributes,
          ...DEFAULT_MOBILE_LINKED_ITEM_ATTRIBUTES,
          visual: media ? 'primary' : 'hidden'
        }
      } as MobileLinkedItem;
    } else {
      linkedItem = sharedValues as LinkedItem;
    }
    if (typedImageSnippetyData !== null) {
      const {
        type,
        tools: {
          image: { caption, credit, reuseType, height, width, src, href, aspectRatio, id }
        }
      } = typedImageSnippetyData;
      linkedItem.attributes.image = {
        type: 'image',
        credit,
        media_type: type,
        name: href,
        reuse_type: reuseType,
        height,
        width,
        alternate_text: caption,
        id,
        alt_images: [],
        properties: {
          imphotoid: (src ? src.imageId : '') ?? typedImageSnippetyData.tools.image.id,
          location: href
        },
        src: {
          image_id: src?.imageId ?? '',
          path: src?.imageId ?? '',
          base_url: src?.baseUrl ?? '',
          params: src?.params ?? { size: aspectRatio }
        }
      };
    }

    if (isEditingExistingLinkedItem) {
      modifyEntity(hierarchyId, (entity) => {
        Object.assign(entity, linkedItem);
      });
    } else {
      removeEntity(hierarchyId);
      insertEntity(hierarchyId, linkedItem);
    }

    closeEditingForm();
    scrollToItem(hierarchyId);
  };

  const graphicsSubmitDisabled = !!graphicUrlError || !graphicUrl;

  const submitDisabled = !!(
    !!validation.length ||
    !!(urlValidationError && urlValidationError !== urlErrorMessages.notFound) ||
    !!mediaValidationError ||
    (!isEditingExistingLinkedItem && !isBlockedDomain && imageValue !== media) ||
    (!isEditingExistingLinkedItem && !isBlockedDomain && urlValue !== currentUrl) ||
    (!isEditingExistingLinkedItem && !isBlockedDomain && isFetchingImageDataFromSnippety) ||
    (!isEditingExistingLinkedItem && !isBlockedDomain && isFetchingUrlDataFromSnippety)
  );

  let title = 'Add Linked Item';

  if (isEditingExistingLinkedItem && !isReadOnly) {
    title = 'Edit Linked Item';
  } else if (isReadOnly) {
    title = 'Linked Item';
  }

  const isGraphicsCard = cardType === LinkedItemCardType.Graphics;
  return (
    <div ref={formRef}>
      <Box>
        <Box.Title className={styles.title} padding={{ left: 'md', right: 'sm', vertical: 'md' }}>
          {title}
        </Box.Title>

        <Box.Content padding={{ all: 'md' }}>
          <Select
            name="cardType"
            value={cardType}
            disabled={isEditingExistingLinkedItem || isReadOnly}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setCardType(e.target.value as LinkedItemCardType);
              setErrors([]);
            }}
          >
            <option value={LinkedItemCardType.Standard}>Standard Card</option>
            <option value={LinkedItemCardType.Graphics}>Graphics Card</option>
          </Select>

          <FormHelperText margin={{ top: 'md' }}>
            {isGraphicsCard
              ? 'Graphics cards are intended for large (L) and medium (M) zones.'
              : ' This is the default card type. Intended for small (S) zone.'}
          </FormHelperText>
        </Box.Content>
        <Divider />
        <Box.Content padding={{ all: 'md' }}>
          {!!errors.length && (
            <Alert onDismiss={() => setErrors([])} type="error">
              The following field{errors.length > 1 ? 's are' : ' is'} empty or invalid: {errors.join(', ')}.
            </Alert>
          )}
          {isGraphicsCard ? (
            <Group direction="column" gap="md">
              <FormLabel fullWidth label="Dynamic inset" required>
                <Input
                  error={errors.includes('url') || !!graphicUrlError}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setGraphicUrl(e.target.value);
                    setGraphicUrlError('');
                  }}
                  placeholder="Add .JSON file for the dynamic inset"
                  value={graphicUrl}
                  disabled={isReadOnly}
                />
                {graphicUrlError && <FormHelperText error>{graphicUrlError}</FormHelperText>}
              </FormLabel>
              <Group gap="md" fullWidth>
                <Group.Item fullWidth>
                  <FormLabel fullWidth label="CTA Link">
                    <Input
                      error={errors.includes('ctaLink')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCtaLink(e.target.value)}
                      placeholder="Add URL"
                      value={ctaLink}
                      disabled={isReadOnly}
                    />
                    {errors.includes('ctaLink') && <FormHelperText error>{ctaLinkError}</FormHelperText>}
                  </FormLabel>
                </Group.Item>
                <Group.Item fullWidth>
                  <FormLabel fullWidth label="CTA Text">
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCtaText(e.target.value)}
                      placeholder="Add CTA text"
                      value={ctaText}
                      disabled={isReadOnly}
                    />
                  </FormLabel>
                </Group.Item>
              </Group>
              <Group align="end" fullWidth valign="end">
                <Group gap="md">
                  <Button
                    onClick={() => {
                      if (!isEditingExistingLinkedItem) {
                        removeEntity(hierarchyId);
                      }
                      closeEditingForm();
                    }}
                    secondary
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (graphicsValidation.length) {
                        setErrors(graphicsValidation);
                      } else {
                        handleSubmit();
                      }
                    }}
                    disabled={graphicsSubmitDisabled || isReadOnly}
                    primary
                  >
                    Save linked item
                  </Button>
                </Group>
              </Group>
            </Group>
          ) : (
            <Group direction="column" gap="md">
              <FormLabel fullWidth label="Card link" required>
                <Input
                  error={
                    !!(
                      errors.includes('url') ||
                      !!(urlValidationError && urlErrorMessages.notFound !== urlValidationError)
                    )
                  }
                  onBlur={() => handleUrlOnBlur()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentUrl(e.target.value)}
                  onPaste={() => handleUrlPaste()}
                  value={currentUrl}
                  placeholder="Add URL"
                  disabled={isFetchingUrlDataFromSnippety || isReadOnly}
                />
                {urlValidationError && (
                  <FormHelperText error={urlErrorMessages.notFound !== urlValidationError}>
                    {urlValidationError}
                  </FormHelperText>
                )}
                {showBypassValidationCheckbox && (
                  <FormLabel label="Bypass Validation" labelPosition="right">
                    <Checkbox
                      name="Bypass Validation"
                      checked={bypassValidation}
                      onChange={handleBypassValidation}
                      disabled={isReadOnly}
                    />
                  </FormLabel>
                )}
              </FormLabel>
              <FormLabel fullWidth label="Headline" required={!disableMetadata}>
                <Input
                  error={errors.includes('headline')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeadline(e.target.value)}
                  placeholder="Headline"
                  value={headline}
                  disabled={isFetchingUrlDataFromSnippety || disableMetadata || isReadOnly}
                />
              </FormLabel>
              <FormLabel fullWidth label="Image URL">
                <Input
                  error={!!(errors.includes('media') || mediaValidationError)}
                  onBlur={() => handleImageOnBlur()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setMedia(e.target.value);
                    if (ONLY_IM_ID.test(e.target.value) || !e.target.value) {
                      setMediaValidationError('');
                    }
                  }}
                  placeholder="Add Image Manager URL"
                  value={media}
                  disabled={isFetchingImageDataFromSnippety || disableMetadata || isReadOnly}
                />
                {(errors.includes('media') || mediaValidationError) && (
                  <FormHelperText error>{mediaValidationError}</FormHelperText>
                )}
                <FormHelperText className="lc-st_formhelpertext image-action-buttons">
                  <ImageManagerEmbed disabled={disableMetadata || isReadOnly} callback={embedCallback} />
                  in Image Manager.
                </FormHelperText>
              </FormLabel>
              <FormLabel fullWidth label="Summary">
                <Textarea
                  error={errors.includes('summary')}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSummary(e.target.value)}
                  placeholder="Summary"
                  value={summary}
                  disabled={isFetchingUrlDataFromSnippety || disableMetadata || isReadOnly}
                />
                <Group.Item align="flex-end">
                  <FormHelperText>
                    {totalWords} {totalWords === 1 ? 'word' : 'words'}
                  </FormHelperText>
                </Group.Item>
              </FormLabel>

              <Group align="end" fullWidth valign="end">
                <Group gap="md">
                  <Button
                    onClick={() => {
                      if (!isEditingExistingLinkedItem) {
                        removeEntity(hierarchyId);
                      }
                      closeEditingForm();
                    }}
                    secondary
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (validation.length) {
                        setErrors(validation);
                      } else {
                        handleSubmit();
                      }
                    }}
                    disabled={submitDisabled || isReadOnly}
                    primary
                  >
                    Save linked item
                  </Button>
                </Group>
              </Group>
            </Group>
          )}
        </Box.Content>
      </Box>
    </div>
  );
};
